<template>
  <div>
    <e-file-load-area
      :accept="accept"
      :multiple="multiple"
      :max-size="maxSize"
      :subtitle="subtitle"
      :error-message="filesErrorMessage"
      :disabled="disabled"
      @upload="onUpload"
    >
      <template #counter>
        <slot name="counter" />
      </template>
      <template #warning>
        <slot name="warning" />
      </template>
      <template #subtitle>
        <slot name="subtitle" />
      </template>
    </e-file-load-area>

    <files-gallery v-if="modelValue.length" :files="modelValue" deletable class="mt-4" @delete="deleteFile" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue';
import { EFileLoadArea } from '@/core/components/ui';
import filesGallery from '@/core/components/ui/files/filesGallery.vue';
import { type LoadedFile, type ProcessFileRules } from '@/core/types/files';
import { formatBytes, getMbFromBite, processFile } from '@/core/utils/file';
import { declensionString } from '@/core/utils/string';

const props = defineProps<{
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxTotalSize?: number;
  maxFilesCount?: number;
  subtitle?: string;
  rules?: ProcessFileRules;
  deleteImageFunction?: (file: LoadedFile) => Promise<boolean>;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'load', files: LoadedFile[]): void;
  (e: 'delete', file: LoadedFile): void;
}>();

const modelValue = defineModel<LoadedFile[]>({ required: true });

defineSlots<{
  subtitle?: () => void;
  counter?: () => void;
  warning?: () => void;
}>();

const onUpload = async (files: File[]) => {
  for (const item of files) {
    const processedFile = await processFile(item, props.rules);

    modelValue.value.push(processedFile);
  }

  await nextTick();
  emit('load', modelValue.value);
};

const deleteFile = async (id: string) => {
  let isDeleted = false;

  const file = modelValue.value.find((file) => file.id === id);

  if (!file) {
    return;
  }

  if (props.deleteImageFunction) {
    file.loading = true;
    isDeleted = await props.deleteImageFunction(file);
    file.loading = false;
  }

  if (!isDeleted) {
    return;
  }

  modelValue.value = modelValue.value.filter((file) => file.id !== id);

  emit('delete', file);
};

const totalSize = computed(() => getMbFromBite(modelValue.value.reduce((res, file) => (res += file.size), 0)));

const filesErrorMessage = computed(() => {
  if (props.maxFilesCount && modelValue.value.length > props.maxFilesCount) {
    return `Максимум ${props.maxFilesCount} ${declensionString(props.maxFilesCount, ['файл', 'файла', 'файлов'])}`;
  }

  if (props.maxTotalSize && totalSize.value > props.maxTotalSize) {
    return `Общий вес файлов не может превышать ${formatBytes(props.maxTotalSize)}`;
  }

  return '';
});
</script>

<style scoped lang="scss"></style>
